import React, { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { auditPdpEndpoint } from "../../api/endpoints";
import { PdpAuditTool } from "../../components/PdpAudit/PdpAuditTool";
import { PdpAuditResponse } from "../../components/PdpAudit/types";
import { PAGE_BG } from "../../components/ProposalFlow/theme";
import { useUser } from "../../hooks/useUser";
import { isInternal } from "../../utils";
import { fireLinkedInConversion } from "../../utils/linkedInTracker";

// Public /pdp-audit page — a thin wrapper around the reusable PdpAuditTool
// (src/components/PdpAudit/PdpAuditTool.tsx) that keeps the historical
// public behavior: captcha always on, email required when logged out,
// 3/day localStorage limit unless internal, ?bg= embed override, the
// pdp-audit-embed body class, and LinkedIn conversion tracking.

// LinkedIn Insight Tag conversion id for the PDP audit submission.
// fireLinkedInConversion handles the production check, per-browser
// localStorage dedupe, and the typeof-lintrk safety check.
const LINKEDIN_PDP_AUDIT_CONVERSION_ID = 25711564;

function parseBgParam(raw: string | null): string {
    if (!raw) return PAGE_BG;
    const cleaned = raw.replace(/^#/, "").trim();
    if (/^[0-9a-fA-F]{3}$/.test(cleaned) || /^[0-9a-fA-F]{6}$/.test(cleaned)) {
        return `#${cleaned}`;
    }
    return PAGE_BG;
}

export default function PdpAuditPage() {
    const [searchParams] = useSearchParams();
    const { user, role } = useUser();
    const bypassLimit = isInternal(role);
    const isLoggedIn = !!user;

    const bgColor = useMemo(() => parseBgParam(searchParams.get("bg")), [searchParams]);

    useEffect(() => {
        document.body.classList.add("pdp-audit-embed");
        return () => {
            document.body.classList.remove("pdp-audit-embed");
        };
    }, []);

    const onReportCreated = useCallback((data: PdpAuditResponse) => {
        // Fire the LinkedIn conversion only when a real report mounts —
        // the API can return successfully but with meta.geminiStatus
        // === "error" (default-shaped audit, warning banner shown);
        // those don't count as conversions.
        if (data.meta.geminiStatus === "success") {
            fireLinkedInConversion(LINKEDIN_PDP_AUDIT_CONVERSION_ID, "pdpAudit");
        }
    }, []);

    return (
        <PdpAuditTool
            endpoint={auditPdpEndpoint}
            showCaptcha
            requireEmail={!isLoggedIn}
            enforceDailyLimit={!bypassLimit}
            showPortalCta={!isLoggedIn}
            bgColor={bgColor}
            onReportCreated={onReportCreated}
        />
    );
}
